import { useCallback } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AcuityAvailableDate {
  date: string; // "YYYY-MM-DD"
}

export interface AcuityAvailableTime {
  time: string; // ISO 8601 datetime
}

export interface AcuityFieldValue {
  id: number;
  value: string;
}

export interface AcuityAppointment {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  date: string;
  time: string;
  endTime: string;
  datetime: string;
  appointmentTypeID: number;
  calendarID: number;
  calendar: string;
  duration: string;
  timezone: string;
  type: string;
}

export interface AcuityCalendar {
  id: number;
  name: string;
  email: string;
  description?: string;
  timezone?: string;
  firstAvailableDate?: string;
}

// ---------------------------------------------------------------------------
// GraphQL documents
// ---------------------------------------------------------------------------

const GET_AVAILABLE_DATES = gql`
  query GetAcuityAvailableDates(
    $orderUidPublic: String!
    $testUid: String!
    $appointmentTypeID: Int!
    $month: String!
    $calendarID: Int
    $timezone: String
  ) {
    getAcuityAvailableDates(
      orderUidPublic: $orderUidPublic
      testUid: $testUid
      appointmentTypeID: $appointmentTypeID
      month: $month
      calendarID: $calendarID
      timezone: $timezone
    ) {
      date
    }
  }
`;

const GET_AVAILABLE_TIMES = gql`
  query GetAcuityAvailableTimes(
    $orderUidPublic: String!
    $testUid: String!
    $appointmentTypeID: Int!
    $date: String!
    $calendarID: Int
    $timezone: String
  ) {
    getAcuityAvailableTimes(
      orderUidPublic: $orderUidPublic
      testUid: $testUid
      appointmentTypeID: $appointmentTypeID
      date: $date
      calendarID: $calendarID
      timezone: $timezone
    ) {
      time
    }
  }
`;

const CREATE_APPOINTMENT = gql`
  mutation CreateAcuityAppointment(
    $orderUidPublic: String!
    $testUid: String!
    $input: CreateAcuityAppointmentInput!
  ) {
    createAcuityAppointment(
      orderUidPublic: $orderUidPublic
      testUid: $testUid
      input: $input
    ) {
      id
      firstName
      lastName
      email
      phone
      date
      time
      endTime
      datetime
      appointmentTypeID
      calendarID
      calendar
      duration
      timezone
      type
    }
  }
`;

const GET_CALENDARS = gql`
  query GetAcuityCalendars(
    $orderUidPublic: String!
    $testUid: String!
    $appointmentTypeID: Int!
    $timezone: String
  ) {
    getAcuityCalendars(
      orderUidPublic: $orderUidPublic
      testUid: $testUid
      appointmentTypeID: $appointmentTypeID
      timezone: $timezone
    ) {
      id
      name
      email
      description
      timezone
      firstAvailableDate
    }
  }
`;

// ---------------------------------------------------------------------------
// NonAuth (guest) GraphQL documents
//
// Token-authorized equivalents used by the anonymous order-lookup
// (Public_Entry_Order_Lookup) GIS funnel. They authorize via the signed guest
// token bound to a single test (`testUid`) instead of `orderUidPublic`.
// ---------------------------------------------------------------------------

const GET_AVAILABLE_DATES_NONAUTH = gql`
  query GetAcuityAvailableDatesNonAuth(
    $token: String!
    $testUid: String!
    $appointmentTypeID: Int!
    $month: String!
    $calendarID: Int
    $timezone: String
  ) {
    getAcuityAvailableDatesNonAuth(
      token: $token
      testUid: $testUid
      appointmentTypeID: $appointmentTypeID
      month: $month
      calendarID: $calendarID
      timezone: $timezone
    ) {
      date
    }
  }
`;

const GET_AVAILABLE_TIMES_NONAUTH = gql`
  query GetAcuityAvailableTimesNonAuth(
    $token: String!
    $testUid: String!
    $appointmentTypeID: Int!
    $date: String!
    $calendarID: Int
    $timezone: String
  ) {
    getAcuityAvailableTimesNonAuth(
      token: $token
      testUid: $testUid
      appointmentTypeID: $appointmentTypeID
      date: $date
      calendarID: $calendarID
      timezone: $timezone
    ) {
      time
    }
  }
`;

const CREATE_APPOINTMENT_NONAUTH = gql`
  mutation CreateAcuityAppointmentNonAuth(
    $token: String!
    $testUid: String!
    $input: CreateAcuityAppointmentInput!
  ) {
    createAcuityAppointmentNonAuth(
      token: $token
      testUid: $testUid
      input: $input
    ) {
      id
      firstName
      lastName
      email
      phone
      date
      time
      endTime
      datetime
      appointmentTypeID
      calendarID
      calendar
      duration
      timezone
      type
    }
  }
`;

const GET_CALENDARS_NONAUTH = gql`
  query GetAcuityCalendarsNonAuth(
    $token: String!
    $testUid: String!
    $appointmentTypeID: Int!
    $timezone: String
  ) {
    getAcuityCalendarsNonAuth(
      token: $token
      testUid: $testUid
      appointmentTypeID: $appointmentTypeID
      timezone: $timezone
    ) {
      id
      name
      email
      description
      timezone
      firstAvailableDate
    }
  }
`;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Variables shared by the authenticated and guest scheduling fetchers.
 *
 * `orderUidPublic` scopes the authenticated call; pass a guest `token` instead
 * to route through the NonAuth resolvers (the token binds the call to a single
 * `testUid`, so `orderUidPublic` is ignored in that case).
 */
interface AcuityFetchBase {
  orderUidPublic?: string;
  testUid: string;
  appointmentTypeID: number;
  calendarID?: number;
  timezone?: string;
  token?: string;
}

interface UseAvailableDatesResult {
  fetchDates: (vars: AcuityFetchBase & { month: string }) => void;
  dates: AcuityAvailableDate[];
  loading: boolean;
  error?: string;
}

export function useAcuityAvailableDates(): UseAvailableDatesResult {
  const [execAuth, authState] = useLazyQuery<{
    getAcuityAvailableDates: AcuityAvailableDate[];
  }>(GET_AVAILABLE_DATES, { fetchPolicy: "network-only" });
  const [execGuest, guestState] = useLazyQuery<{
    getAcuityAvailableDatesNonAuth: AcuityAvailableDate[];
  }>(GET_AVAILABLE_DATES_NONAUTH, { fetchPolicy: "network-only" });

  const fetchDates = useCallback(
    (vars: AcuityFetchBase & { month: string }) => {
      if (vars.token) {
        execGuest({
          variables: {
            token: vars.token,
            testUid: vars.testUid,
            appointmentTypeID: vars.appointmentTypeID,
            month: vars.month,
            calendarID: vars.calendarID,
            timezone: vars.timezone,
          },
        });
        return;
      }
      execAuth({
        variables: {
          orderUidPublic: vars.orderUidPublic,
          testUid: vars.testUid,
          appointmentTypeID: vars.appointmentTypeID,
          month: vars.month,
          calendarID: vars.calendarID,
          timezone: vars.timezone,
        },
      });
    },
    [execAuth, execGuest]
  );

  return {
    fetchDates,
    dates:
      guestState.data?.getAcuityAvailableDatesNonAuth ??
      authState.data?.getAcuityAvailableDates ??
      [],
    loading: authState.loading || guestState.loading,
    error: authState.error?.message ?? guestState.error?.message,
  };
}

interface UseAvailableTimesResult {
  fetchTimes: (vars: AcuityFetchBase & { date: string }) => void;
  times: AcuityAvailableTime[];
  loading: boolean;
  error?: string;
}

export function useAcuityAvailableTimes(): UseAvailableTimesResult {
  const [execAuth, authState] = useLazyQuery<{
    getAcuityAvailableTimes: AcuityAvailableTime[];
  }>(GET_AVAILABLE_TIMES, { fetchPolicy: "network-only" });
  const [execGuest, guestState] = useLazyQuery<{
    getAcuityAvailableTimesNonAuth: AcuityAvailableTime[];
  }>(GET_AVAILABLE_TIMES_NONAUTH, { fetchPolicy: "network-only" });

  const fetchTimes = useCallback(
    (vars: AcuityFetchBase & { date: string }) => {
      if (vars.token) {
        execGuest({
          variables: {
            token: vars.token,
            testUid: vars.testUid,
            appointmentTypeID: vars.appointmentTypeID,
            date: vars.date,
            calendarID: vars.calendarID,
            timezone: vars.timezone,
          },
        });
        return;
      }
      execAuth({
        variables: {
          orderUidPublic: vars.orderUidPublic,
          testUid: vars.testUid,
          appointmentTypeID: vars.appointmentTypeID,
          date: vars.date,
          calendarID: vars.calendarID,
          timezone: vars.timezone,
        },
      });
    },
    [execAuth, execGuest]
  );

  return {
    fetchTimes,
    times:
      guestState.data?.getAcuityAvailableTimesNonAuth ??
      authState.data?.getAcuityAvailableTimes ??
      [],
    loading: authState.loading || guestState.loading,
    error: authState.error?.message ?? guestState.error?.message,
  };
}

interface CreateAppointmentVars {
  orderUidPublic?: string;
  testUid: string;
  token?: string;
  input: {
    datetime: string;
    appointmentTypeID: number;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    timezone?: string;
    calendarID?: number;
    fields?: AcuityFieldValue[];
  };
}

interface UseCreateAppointmentResult {
  createAppointment: (
    vars: CreateAppointmentVars
  ) => Promise<AcuityAppointment | null>;
  appointment: AcuityAppointment | null;
  loading: boolean;
  error?: string;
}

export function useCreateAcuityAppointment(): UseCreateAppointmentResult {
  const [execAuth, authState] = useMutation<{
    createAcuityAppointment: AcuityAppointment;
  }>(CREATE_APPOINTMENT);
  const [execGuest, guestState] = useMutation<{
    createAcuityAppointmentNonAuth: AcuityAppointment;
  }>(CREATE_APPOINTMENT_NONAUTH);

  const createAppointment = useCallback(
    async (vars: CreateAppointmentVars): Promise<AcuityAppointment | null> => {
      if (vars.token) {
        const result = await execGuest({
          variables: {
            token: vars.token,
            testUid: vars.testUid,
            input: vars.input,
          },
        });
        return result.data?.createAcuityAppointmentNonAuth ?? null;
      }
      const result = await execAuth({
        variables: {
          orderUidPublic: vars.orderUidPublic,
          testUid: vars.testUid,
          input: vars.input,
        },
      });
      return result.data?.createAcuityAppointment ?? null;
    },
    [execAuth, execGuest]
  );

  return {
    createAppointment,
    appointment:
      guestState.data?.createAcuityAppointmentNonAuth ??
      authState.data?.createAcuityAppointment ??
      null,
    loading: authState.loading || guestState.loading,
    error: authState.error?.message ?? guestState.error?.message,
  };
}

interface UseAcuityCalendarsResult {
  fetchCalendars: (vars: Omit<AcuityFetchBase, "calendarID">) => void;
  calendars: AcuityCalendar[];
  loading: boolean;
  error?: string;
}

export function useAcuityCalendars(): UseAcuityCalendarsResult {
  const [execAuth, authState] = useLazyQuery<{
    getAcuityCalendars: AcuityCalendar[];
  }>(GET_CALENDARS, { fetchPolicy: "network-only" });
  const [execGuest, guestState] = useLazyQuery<{
    getAcuityCalendarsNonAuth: AcuityCalendar[];
  }>(GET_CALENDARS_NONAUTH, { fetchPolicy: "network-only" });

  const fetchCalendars = useCallback(
    (vars: Omit<AcuityFetchBase, "calendarID">) => {
      if (vars.token) {
        execGuest({
          variables: {
            token: vars.token,
            testUid: vars.testUid,
            appointmentTypeID: vars.appointmentTypeID,
            timezone: vars.timezone,
          },
        });
        return;
      }
      execAuth({
        variables: {
          orderUidPublic: vars.orderUidPublic,
          testUid: vars.testUid,
          appointmentTypeID: vars.appointmentTypeID,
          timezone: vars.timezone,
        },
      });
    },
    [execAuth, execGuest]
  );

  return {
    fetchCalendars,
    calendars:
      guestState.data?.getAcuityCalendarsNonAuth ??
      authState.data?.getAcuityCalendars ??
      [],
    loading: authState.loading || guestState.loading,
    error: authState.error?.message ?? guestState.error?.message,
  };
}
